import { StandardEvents } from '@shopify/events';

/**
 * <free-shipping-bar>
 * Keeps the free-shipping message + progress bar in sync with the live cart.
 * Initial state is rendered server-side by sections/free-shipping-bar.liquid;
 * this component refreshes it whenever the cart changes.
 */
class FreeShippingBar extends HTMLElement {
  connectedCallback() {
    this.textEl = this.querySelector('[data-fsb-text]');
    this.fillEl = this.querySelector('[data-fsb-fill]');

    this.threshold = parseInt(this.dataset.threshold, 10) || 0;
    this.hasGoal = this.dataset.hasGoal === 'true' && this.threshold > 0;
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.templates = {
      initial: this.dataset.textInitial || '',
      progress: this.dataset.textProgress || '',
      achieved: this.dataset.textAchieved || '',
    };

    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handlePageShow = this.handlePageShow.bind(this);

    document.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate);
    // Discount codes change cart.total_price too, but Horizon's discount box
    // fires its own event rather than cartLinesUpdate — without this the bar
    // kept showing "free delivery" after a code took the cart under the goal.
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.handleCartUpdate);
    window.addEventListener('pageshow', this.handlePageShow);

    // Correct any stale server render (cached pages) once on load.
    this.refresh();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.handleCartUpdate);
    window.removeEventListener('pageshow', this.handlePageShow);
  }

  handlePageShow(event) {
    if (event.persisted) this.refresh();
  }

  handleCartUpdate(event) {
    // Wait for the cart mutation to settle when a promise is provided.
    if (event && event.promise && typeof event.promise.then === 'function') {
      event.promise.then(() => this.refresh()).catch(() => this.refresh());
    } else {
      this.refresh();
    }
  }

  refresh() {
    fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      headers: { Accept: 'application/json' },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        if (cart) this.render(cart.total_price);
      })
      .catch(() => {
        /* keep the server-rendered state on failure */
      });
  }

  render(total) {
    let state = 'achieved';
    let remaining = 0;
    let percent = 100;

    if (this.hasGoal) {
      remaining = Math.max(this.threshold - total, 0);
      percent = Math.min(100, Math.max(0, (total / this.threshold) * 100));
      if (total <= 0) state = 'initial';
      else if (remaining > 0) state = 'progress';
      else state = 'achieved';
    }

    let html = this.templates[state] || '';
    if (state === 'progress') {
      const money = formatMoney(remaining, this.moneyFormat);
      html = html.replace(/\[\s*amount_left\s*\]|\{\{\s*amount_left\s*\}\}/g, money);
    }

    if (this.textEl) this.textEl.innerHTML = html;
    if (this.fillEl) {
      this.fillEl.style.width = `${percent}%`;
      const track = this.fillEl.closest('.free-shipping-bar__track');
      if (track) track.setAttribute('aria-valuenow', String(Math.round(percent)));
    }
    this.dataset.state = state;
  }
}

/**
 * Shopify-compatible money formatter.
 * @param {number} cents
 * @param {string} format - shop.money_format string, e.g. "${{amount}}"
 * @returns {string}
 */
function formatMoney(cents, format) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || '${{amount}}';

  const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
    if (isNaN(number) || number == null) return '0';
    const value = (number / 100).toFixed(precision);
    const parts = value.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const centsPart = parts[1] ? decimal + parts[1] : '';
    return dollars + centsPart;
  };

  const match = formatString.match(placeholderRegex);
  const placeholder = match ? match[1] : 'amount';

  let value;
  switch (placeholder) {
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_with_space_separator':
      value = formatWithDelimiters(cents, 2, ' ', ',');
      break;
    case 'amount':
    default:
      value = formatWithDelimiters(cents, 2);
      break;
  }

  return formatString.replace(placeholderRegex, value);
}

if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
}
